import { useRef } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { useMousePosition } from '../../hooks/useMousePosition'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import buttonStyles from '../Button/Button.module.css'

interface MagneticButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode
  variant?: 'primary' | 'secondary' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  strength?: number
}

export function MagneticButton({
  children,
  variant = 'primary',
  size = 'md',
  strength = 10,
  className = '',
  ...props
}: MagneticButtonProps) {
  const ref = useRef<HTMLDivElement>(null)
  const { x, y, isHovering } = useMousePosition(ref)
  const prefersReduced = useReducedMotion()

  const translateX = prefersReduced ? 0 : isHovering ? x * strength : 0
  const translateY = prefersReduced ? 0 : isHovering ? y * strength : 0

  // Resolve button classes — outline on dark hero uses inverse variant
  const variantClass = variant === 'outline'
    ? buttonStyles.outlineInverse
    : buttonStyles[variant]

  const classNames = [
    buttonStyles.button,
    variantClass,
    buttonStyles[size],
    className,
  ].filter(Boolean).join(' ')

  return (
    <motion.div
      ref={ref}
      animate={{ x: translateX, y: translateY }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      style={{ display: 'inline-block' }}
    >
      <button className={classNames} {...props}>
        {children}
      </button>
    </motion.div>
  )
}
